import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { addMonths, subMonths } from 'date-fns';
import { scaleTime } from '@visx/scale';
import { debounce } from 'lodash';
import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';
import { useToast } from '../context/ToastContext';
import { useBusinessMetrics } from './useStatsigFeatures';
import { useAuth } from '../context/AuthContext';

// Import required types
import type {
  TimelineItem,
  TimelineItemType,
  TimelineError,
  HoverPosition
} from '../types/timeline';

// Constants
const ALL_ITEM_TYPES: TimelineItemType[] = ['note', 'careerCard', 'resume', 'review', 'workExperience', 'file', 'retrospective'];
const logger = createLogger({ module: 'useTimelineState', level: 'info' });

/**
 * Custom hook for managing all timeline state
 */
export function useTimelineState(initialData: TimelineItem[] = []) {
  // Statsig analytics hook
  const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();
  
  // Core data state
  const [timelineData, setTimelineData] = useState<TimelineItem[]>(initialData);
  const [isLoading, setIsLoading] = useState(initialData.length === 0);
  const [errors, setErrors] = useState<TimelineError[]>([]);
  const [userName, setUserName] = useState<string>('');
  const [visibleTypes, setVisibleTypes] = useState<Set<TimelineItemType>>(new Set(ALL_ITEM_TYPES));
  
  // Hover/interaction state
  const [hoveredItem, setHoveredItem] = useState<TimelineItem | null>(null);
  const [hoverPosition, setHoverPosition] = useState<HoverPosition>({ 
    x: 0, 
    y: 0, 
    isNearRightEdge: false, 
    isNearLeftEdge: false, 
    isNearTopEdge: false 
  });
  
  // Dimensions state (consolidated)
  const [dimensions, setDimensions] = useState({
    containerWidth: 0,
    containerHeight: 0,
    contentHeight: 0
  });
  
  // View state (consolidated)
  const [viewState, setViewState] = useState({
    visibleTimeRange: { start: new Date(), end: new Date() },
    zoomLevel: 0,
    position: 0
  });
  
  // Auth state
  const { user, userProfile, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  
  // Derived values with useMemo
  const timelineBounds = useMemo(() => {
    if (timelineData.length === 0) {
      return { start: subMonths(new Date(), 1), end: addMonths(new Date(), 1) };
    }
    
    const sortedData = [...timelineData].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    const start = subMonths(sortedData[0]?.startDate || new Date(), 1);
    const lastItem = sortedData[sortedData.length - 1];
    const end = addMonths(lastItem ? (lastItem.endDate || lastItem.startDate) : new Date(), 1);
    
    return { start, end };
  }, [timelineData]);
  
  // Sort data only when source data changes
  const sortedData = useMemo(() => {
    return [...timelineData].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, [timelineData]);
  
  // Work experience data
  const workExperienceRows = useMemo(() => {
    const workExperiences = timelineData.filter(item => item.type === 'workExperience');
    return workExperiences.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  }, [timelineData]);
  
  // Stable scale calculation
  const xScale = useMemo(() => 
    scaleTime({
      domain: [viewState.visibleTimeRange.start, viewState.visibleTimeRange.end],
      range: [0, Math.max(0, dimensions.containerWidth - 60)]
    }),
    [viewState.visibleTimeRange, dimensions.containerWidth]
  );

  // Calculate visible data
  const visibleData = useMemo(() => {
    // All items that match the visible types filter
    const filteredData = timelineData.filter(item => visibleTypes.has(item.type));
    
    // Separate work experiences from other items
    const workExperiences = filteredData
      .filter(item => item.type === 'workExperience')
      .sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
      
    const otherItems = filteredData.filter(item => item.type !== 'workExperience');
    
    // Only include items that are visible in the current timeframe (with buffer)
    const isVisible = (item: TimelineItem) => {
      // Always show work experience items
      if (item.type === 'workExperience') {
        return true;
      }
      
      const itemStart = item.startDate.getTime();
      const itemEnd = (item.endDate || item.startDate).getTime();
      const viewStart = viewState.visibleTimeRange.start.getTime();
      const viewEnd = viewState.visibleTimeRange.end.getTime();
      
      // Add a 10% buffer on either side for smoother scroll transitions
      const buffer = (viewEnd - viewStart) * 0.1;
      const bufferedStart = viewStart - buffer;
      const bufferedEnd = viewEnd + buffer;
      
      return (itemStart >= bufferedStart && itemStart <= bufferedEnd) || // Start date in range
             (itemEnd >= bufferedStart && itemEnd <= bufferedEnd) ||     // End date in range
             (itemStart <= bufferedStart && itemEnd >= bufferedEnd);     // Item spans entire range
    };
    
    return {
      workExperiences: workExperiences.filter(isVisible),
      otherItems: otherItems.filter(isVisible)
    };
  }, [timelineData, visibleTypes, viewState.visibleTimeRange]);
  
  // Error handling
  const handleError = useCallback((error: unknown, dataType?: TimelineError['dataType']) => {
    const newError: TimelineError = {
      type: 'data_fetch', // Simplifying the error types for now
      dataType,
      message: error instanceof Error ? error.message : 'An unknown error occurred',
      retryable: true
    };
    
    setErrors(prev => [...prev, newError]);
    
    // Check the proper format of showToast and fix it
    const errorMessage = `Failed to load ${dataType || 'timeline'} data`;
    
    // Use the actual format expected by showToast
    showToast(
      errorMessage,
      'error',
      () => fetchTimelineData()
    );
    
    logger.error('Timeline error:', {
      error: newError,
      details: error,
      context: { dataType }
    });
    
    // Track error in journey
    trackUserJourney('journey_error', {
      journey_id: `timeline_exploration_${Date.now()}`,
      feature_name: 'timeline_exploration',
      error_message: error instanceof Error ? error.message : 'Unknown error'
    });
  }, [showToast]);

  // Data fetching
  const fetchTimelineData = useCallback(async () => {
    const startTime = performance.now();
    setIsLoading(true);
    setErrors([]);
    
    try {
      if (!user) {
        return;
      }

      // Use userProfile if available to avoid extra query
      const firstName = userProfile?.first_name || user.email || 'User';
      setUserName(firstName);
      
      // Fetch data in parallel with error handling for each type
      const [
        contentResult,
        filesResult,
        workResult,
        retrospectivesResult
      ] = await Promise.allSettled([
        supabase.from('user_content_view')
          .select('*')
          .eq('user_id', user.id)
          .in('item_type', ['note', 'review', 'card']),
        supabase.from('files')
          .select('*')
          .eq('user_id', user.id)
          .is('deleted_at', null),
        supabase.from('work_experience')
          .select('id, company, position, location, start_date, end_date, is_current, description')
          .eq('user_id', user.id),
        supabase.from('retrospectives')
          .select('id, title, description, framework_type, start_date, end_date, created_at')
          .eq('user_id', user.id)
      ]);

      // Process results and collect data
      const formattedData: TimelineItem[] = [];
      const currentDate = new Date();

      // Process content view items (notes, reviews, cards)
      if (contentResult.status === 'fulfilled' && contentResult.value.data) {
        formattedData.push(...contentResult.value.data.map(item => {
          const baseItem = {
            id: item.item_id,
            title: item.title || 'Untitled',
            startDate: new Date(item.display_date),
            description: item.preview || '',
          };

          switch (item.item_type) {
            case 'note':
              return {
                ...baseItem,
                type: 'note' as const,
              };
            case 'review':
              return {
                ...baseItem,
                type: 'review' as const,
              };
            case 'card':
              return {
                ...baseItem,
                type: 'careerCard' as const,
                endDate: item.metadata?.timeline?.end_date ? new Date(item.metadata.timeline.end_date) : undefined,
              };
            default:
              return null;
          }
        }).filter(Boolean) as TimelineItem[]);
      } else if (contentResult.status === 'rejected') {
        handleError(contentResult.reason, 'content');
      }

      // Process files
      if (filesResult.status === 'fulfilled' && filesResult.value.data) {
        const files = filesResult.value.data;
        
        files.forEach(file => {
          const fileUsage = file.usage || {};
          const isResume = fileUsage.type === 'resume';
          
          formattedData.push({
            id: file.id,
            type: isResume ? 'resume' as const : 'file' as const,
            title: file.file_name || 'Untitled File',
            startDate: new Date(file.created_at),
            description: isResume ? 
              `Resume: ${file.file_name}` : 
              `File: ${file.file_name} (${file.file_type || 'unknown type'})`,
            fileType: file.file_type,
            fileSize: file.file_size,
            fileUsage: file.usage
          });
        });
      } else if (filesResult.status === 'rejected') {
        handleError(filesResult.reason, 'files');
      }

      // Process work experience
      if (workResult.status === 'fulfilled' && workResult.value.data) {
        formattedData.push(...workResult.value.data.map(work => ({
          id: work.id,
          type: 'workExperience' as const,
          title: `${work.position} at ${work.company}`,
          startDate: new Date(work.start_date),
          endDate: work.is_current ? currentDate : (work.end_date ? new Date(work.end_date) : currentDate),
          description: work.description,
          company: work.company,
          position: work.position,
          location: work.location
        })));
      } else if (workResult.status === 'rejected') {
        handleError(workResult.reason, 'work');
      }

      // Process retrospectives
      if (retrospectivesResult.status === 'fulfilled' && retrospectivesResult.value.data) {
        formattedData.push(...retrospectivesResult.value.data.map(retro => ({
          id: retro.id,
          type: 'retrospective' as const,
          title: retro.title || 'Untitled Retrospective',
          startDate: new Date(retro.start_date),
          endDate: new Date(retro.end_date),
          description: retro.description || '',
          frameworkType: retro.framework_type
        })));
      } else if (retrospectivesResult.status === 'rejected') {
        handleError(retrospectivesResult.reason, 'retrospectives');
      }

      // Filter out items with invalid dates
      const validData = formattedData.filter(item => item.startDate && !isNaN(item.startDate.getTime()));
      setTimelineData(validData);

      // Initialize visible time range once we have data
      if (validData.length > 0) {
        // Set initial visible time range based on data
        const minDate = new Date(Math.min(
          ...validData.map(item => item.startDate.getTime())
        ));
        const maxDate = new Date(Math.max(
          ...validData.map(item => (item.endDate || item.startDate).getTime())
        ));
        
        // Add padding
        const start = subMonths(minDate, 1);
        const end = addMonths(maxDate, 1);
        
        setViewState(prev => ({
          ...prev,
          visibleTimeRange: { start, end }
        }));
      }
      
      // Telemetry
      trackFeatureUsage('timeline_data_fetch', 'complete', {
        duration_ms: Math.round(performance.now() - startTime)
      });
    } catch (error) {
      handleError(error);
      
      // Telemetry for errors
      trackFeatureUsage('timeline_data_fetch', 'error', {
        duration_ms: Math.round(performance.now() - startTime)
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, userProfile, handleError]);

  // Initial data fetch
  useEffect(() => {
    if (!authLoading && user && timelineData.length === 0) {
      fetchTimelineData();
    }
  }, [authLoading, user, timelineData.length, fetchTimelineData]);

  // Start journey tracking
  useEffect(() => {
    // Track page view
    trackFeatureUsage('timeline_page', 'view');
    
    // Start timeline exploration journey
    trackUserJourney('journey_started', {
      journey_id: `timeline_exploration_${Date.now()}`,
      feature_name: 'timeline_exploration'
    });
    
    return () => {
      // End journey when leaving page
      trackUserJourney('journey_completed', {
        journey_id: `timeline_exploration_${Date.now()}`,
        feature_name: 'timeline_exploration',
        outcome: 'success'
      });
    };
  }, []);

  // Update zoom level
  const updateZoom = useCallback((newZoomLevel: number) => {
    setViewState(prev => {
      // Ensure zoom is within bounds
      const clampedZoom = Math.max(0, Math.min(100, newZoomLevel));
      
      // Calculate the current visible range duration
      const currentRange = prev.visibleTimeRange.end.getTime() - prev.visibleTimeRange.start.getTime();
      
      // Total timeline duration
      const totalDuration = timelineBounds.end.getTime() - timelineBounds.start.getTime();
      
      // Use a minimum of 4 weeks for the most zoomed in view
      const MIN_RANGE_MS = 28 * 24 * 60 * 60 * 1000; // 4 weeks in milliseconds
      
      // Linear interpolation between total duration at zoom=0 and min duration at zoom=100
      // When zoom = 100, we want exactly 4 weeks
      // When zoom = 0, we want the full timeline
      const rangeFraction = (100 - clampedZoom) / 100;
      const newRangeDuration = MIN_RANGE_MS + rangeFraction * (totalDuration - MIN_RANGE_MS);
      
      // Calculate the center point of the current range
      const currentCenter = prev.visibleTimeRange.start.getTime() + (currentRange / 2);
      
      // Calculate new start and end based on the center and new range
      const newStart = new Date(currentCenter - (newRangeDuration / 2));
      const newEnd = new Date(currentCenter + (newRangeDuration / 2));
      
      return {
        ...prev,
        zoomLevel: clampedZoom,
        visibleTimeRange: {
          start: newStart,
          end: newEnd
        }
      };
    });
  }, [timelineBounds]);

  // Update visible range
  const updateVisibleRange = useCallback((start: Date, end: Date) => {
    // Use a minimum of 4 weeks for the most zoomed in view
    const MIN_RANGE_MS = 28 * 24 * 60 * 60 * 1000; // 4 weeks in milliseconds
    
    // Calculate duration
    let duration = end.getTime() - start.getTime();
    
    // Ensure minimum range
    if (duration < MIN_RANGE_MS) {
      // Expand around the midpoint to reach minimum range
      const midpoint = (start.getTime() + end.getTime()) / 2;
      const newStart = new Date(midpoint - MIN_RANGE_MS / 2);
      const newEnd = new Date(midpoint + MIN_RANGE_MS / 2);
      
      start = newStart;
      end = newEnd;
      duration = MIN_RANGE_MS;
    }
    
    // Calculate zoom level based on visible range
    const totalDuration = timelineBounds.end.getTime() - timelineBounds.start.getTime();
    // When range = MIN_RANGE_MS, zoom should be 100
    // When range = totalDuration, zoom should be 0
    const zoomPercentage = 100 * (1 - (duration - MIN_RANGE_MS) / (totalDuration - MIN_RANGE_MS));
    const clampedZoom = Math.max(0, Math.min(100, zoomPercentage));
    
    setViewState(prev => ({
      ...prev,
      visibleTimeRange: { start, end },
      zoomLevel: clampedZoom
    }));
  }, [timelineBounds]);

  // Reset view to show all data
  const resetView = useCallback(() => {
    // Reset time range to show all data
    setViewState({
      visibleTimeRange: { start: timelineBounds.start, end: timelineBounds.end },
      zoomLevel: 0,
      position: 0
    });
    
    // Reset visible types to show all types
    setVisibleTypes(new Set(ALL_ITEM_TYPES));
    
    // Reset hover state
    setHoveredItem(null);
    setHoverPosition({ 
      x: 0, 
      y: 0, 
      isNearRightEdge: false, 
      isNearLeftEdge: false, 
      isNearTopEdge: false 
    });
    
    // Track reset action
    trackFeatureUsage('timeline_reset', 'click');
  }, [timelineBounds]);

  // Toggle item type visibility
  const toggleItemType = useCallback((type: TimelineItemType) => {
    setVisibleTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      
      // Telemetry
      trackFeatureUsage('timeline_filter', 'click', {
        filter_type: type,
        action: newSet.has(type) ? 'show' : 'hide'
      });
      
      return newSet;
    });
  }, []);

  // Handle hover
  const handleHover = useCallback((item: TimelineItem | null, position?: HoverPosition) => {
    setHoveredItem(item);
    if (position) {
      setHoverPosition(position);
    }
  }, []);

  // Calculate optimal timeline dimensions
  const calculateTimelineHeight = useCallback(() => {
    const ROW_HEIGHT = 38;
    const PADDING = 40;
    const TOP_SECTION_HEIGHT = 50;
    const BOTTOM_SECTION_HEIGHT = 60;
    
    // Calculate based on content
    const workExperienceHeight = workExperienceRows.length * ROW_HEIGHT;
    const totalHeight = TOP_SECTION_HEIGHT + workExperienceHeight + BOTTOM_SECTION_HEIGHT + PADDING;
    
    return Math.max(Math.min(totalHeight, 800), 400); // Min: 400px, Max: 800px
  }, [workExperienceRows.length]);

  return {
    // State
    timelineData,
    sortedData,
    visibleData,
    workExperienceRows,
    isLoading,
    errors,
    userName,
    visibleTypes,
    hoveredItem,
    hoverPosition,
    dimensions,
    viewState,
    timelineBounds,
    xScale,
    
    // Actions
    setDimensions,
    updateZoom,
    updateVisibleRange,
    resetView,
    toggleItemType,
    handleHover,
    fetchTimelineData,
    calculateTimelineHeight,
    
    // Constants
    ALL_ITEM_TYPES
  };
}

export default useTimelineState; 